import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Home from '../Home';
import { listEepromModules, updateEepromModulePrice } from '../eeprom/api';
import { supabase } from '../auth/supabaseClient';
import ServiceSpecsAdmin from '../components/admin/ServiceSpecsAdmin';
import PartnershipSettingsAdmin from '../components/admin/PartnershipSettingsAdmin';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'https://helpmediy.org:3001/api';

const DEFAULT_RAFFLE_SETTINGS = {
  videoUrl: '',
  descriptions: {
    youtube: 'Subscribe on YouTube and connect your channel to enter.',
    instagram:
      'Follow @helpmediy_offical and DM #raffle plus your account email.',
    website: 'Create an account on this site to receive a raffle entry.',
    facebook:
      'Follow the Help Me DIY Facebook page and DM #raffle plus your account email.',
    purchase: 'Complete payment to receive purchase raffle entries.',
  },
  sponsor: {
    enabled: false,
    name: '',
    logoUrl: '',
    instructions: '',
  },
  purchase: {
    entries: 1,
    priceUsd: 5,
  },
};

const Admin = () => {
  const [modules, setModules] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [entriesRefresh, setEntriesRefresh] = useState(0);
  const [raffleStatus, setRaffleStatus] = useState('');
  const [raffleStatusType, setRaffleStatusType] = useState('success');
  const [requests, setRequests] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState('');
  const [settingsStatusType, setSettingsStatusType] = useState('success');
  const [raffleSettings, setRaffleSettings] = useState(DEFAULT_RAFFLE_SETTINGS);
  const [settingsSavedAt, setSettingsSavedAt] = useState('');

  const getAuthHeaders = async () => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) {
      throw new Error('Please sign in to use admin tools.');
    }
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const load = async () => {
      try {
        const result = await listEepromModules();
        setModules(result.modules || []);
      } catch (err) {
        setError(err.message || 'Failed to load module pricing.');
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadRequests = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE}/entry-requests`, { headers });
        if (!res.ok) return;
        const payload = await res.json();
        setRequests(payload.requests || []);
      } catch (err) {
        // ignore for now
      }
    };
    loadRequests();
  }, []);

  useEffect(() => {
    const loadRaffleSettings = async () => {
      try {
        setSettingsLoading(true);
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE}/raffle/settings`, { headers });
        if (!res.ok) throw new Error('Failed to load raffle settings.');
        const payload = await res.json();
        setRaffleSettings({
          videoUrl: payload?.videoUrl || '',
          descriptions: {
            youtube:
              payload?.descriptions?.youtube ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.youtube,
            instagram:
              payload?.descriptions?.instagram ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.instagram,
            website:
              payload?.descriptions?.website ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.website,
            facebook:
              payload?.descriptions?.facebook ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.facebook,
            purchase:
              payload?.descriptions?.purchase ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.purchase,
          },
          sponsor: {
            enabled:
              payload?.sponsor?.enabled ??
              Boolean(
                payload?.sponsor?.name ||
                  payload?.sponsor?.logoUrl ||
                  payload?.sponsor?.instructions
              ),
            name: payload?.sponsor?.name || '',
            logoUrl: payload?.sponsor?.logoUrl || '',
            instructions: payload?.sponsor?.instructions || '',
          },
          purchase: {
            entries: Number(payload?.purchase?.entries) || 1,
            priceUsd: Number(payload?.purchase?.priceUsd) || 5,
          },
        });
      } catch (err) {
        setSettingsStatus(err.message || 'Failed to load raffle settings.');
        setSettingsStatusType('error');
      } finally {
        setSettingsLoading(false);
      }
    };
    loadRaffleSettings();
  }, []);

  const updatePrice = async (moduleKey, priceCents) => {
    setSaving(true);
    setError('');
    try {
      const result = await updateEepromModulePrice({ moduleKey, priceCents });
      setModules((prev) =>
        prev.map((module) =>
          module.moduleKey === moduleKey
            ? { ...module, priceCents: result.priceCents }
            : module
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update price.');
    } finally {
      setSaving(false);
    }
  };

  const saveRaffleSettings = async () => {
    setSettingsSaving(true);
    setSettingsStatus('');
    setSettingsStatusType('success');
    try {
      const headers = await getAuthHeaders();
      const payload = {
        videoUrl: String(raffleSettings.videoUrl || '').trim(),
        descriptions: {
          youtube: String(raffleSettings.descriptions.youtube || '').trim(),
          instagram: String(raffleSettings.descriptions.instagram || '').trim(),
          website: String(raffleSettings.descriptions.website || '').trim(),
          facebook: String(raffleSettings.descriptions.facebook || '').trim(),
          purchase: String(raffleSettings.descriptions.purchase || '').trim(),
        },
        sponsor: {
          enabled: Boolean(raffleSettings.sponsor.enabled),
          name: raffleSettings.sponsor.name.trim(),
          logoUrl: raffleSettings.sponsor.logoUrl.trim(),
          instructions: raffleSettings.sponsor.instructions.trim(),
        },
        purchase: {
          entries: Math.max(1, Math.floor(Number(raffleSettings.purchase.entries) || 1)),
          priceUsd: Math.max(0, Number(raffleSettings.purchase.priceUsd) || 0),
        },
      };
      const res = await fetch(`${API_BASE}/raffle/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to save raffle settings.');
      const saved = await res.json();
      setRaffleSettings({
        videoUrl: saved?.videoUrl || payload.videoUrl,
        descriptions: {
          youtube: saved?.descriptions?.youtube || payload.descriptions.youtube,
          instagram:
            saved?.descriptions?.instagram || payload.descriptions.instagram,
          website: saved?.descriptions?.website || payload.descriptions.website,
          facebook:
            saved?.descriptions?.facebook || payload.descriptions.facebook,
          purchase:
            saved?.descriptions?.purchase || payload.descriptions.purchase,
        },
        sponsor: {
          enabled: saved?.sponsor?.enabled ?? payload.sponsor.enabled,
          name: saved?.sponsor?.name || payload.sponsor.name,
          logoUrl: saved?.sponsor?.logoUrl || payload.sponsor.logoUrl,
          instructions:
            saved?.sponsor?.instructions || payload.sponsor.instructions,
        },
        purchase: {
          entries:
            Number(saved?.purchase?.entries) || payload.purchase.entries,
          priceUsd:
            Number(saved?.purchase?.priceUsd) || payload.purchase.priceUsd,
        },
      });
      const savedAt = saved?.updatedAt
        ? new Date(saved.updatedAt).toLocaleString()
        : new Date().toLocaleString();
      setSettingsSavedAt(savedAt);
      setSettingsStatus('Raffle settings saved.');
      setSettingsStatusType('success');
    } catch (err) {
      setSettingsStatus(err.message || 'Failed to save raffle settings.');
      setSettingsStatusType('error');
    } finally {
      setSettingsSaving(false);
    }
  };

  const seedEntries = async () => {
    setRaffleStatus('');
    setRaffleStatusType('success');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/entries/raffle/seed`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error('Failed to seed entries.');
      const data = await res.json();
      setRaffleStatus(`Seeded ${data.insertedCount || 0} entries.`);
      setRaffleStatusType('success');
      setEntriesRefresh((prev) => prev + 1);
    } catch (err) {
      setRaffleStatus(err.message || 'Failed to seed entries.');
      setRaffleStatusType('error');
    }
  };

  const deleteAllEntries = async () => {
    setConfirmDeleteOpen(false);
    setRaffleStatus('');
    setRaffleStatusType('success');
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/entries`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error('Failed to delete entries.');
      const data = await res.json();
      setRaffleStatus(`Deleted ${data.deletedCount || 0} entries.`);
      setRaffleStatusType('success');
      setEntriesRefresh((prev) => prev + 1);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Delete all entries failed', err);
      setRaffleStatus(err.message || 'Failed to delete entries.');
      setRaffleStatusType('error');
    }
  };

  const approveRequest = async (id) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/entry-requests/${id}/approve`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error('Failed to approve request.');
      setRequests((prev) =>
        prev.map((req) =>
          req._id === id ? { ...req, status: 'approved' } : req
        )
      );
      setEntriesRefresh((prev) => prev + 1);
      setTimeout(() => {
        setRequests((prev) => prev.filter((req) => req._id !== id));
      }, 1000);
    } catch (err) {
      setRaffleStatus(err.message || 'Failed to approve request.');
    }
  };

  const deleteRequest = async (id) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/entry-requests/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error('Failed to delete request.');
      setRequests((prev) => prev.filter((req) => req._id !== id));
    } catch (err) {
      setRaffleStatus(err.message || 'Failed to delete request.');
    }
  };

  return (
    <div className="admin">
      <h2>Admin</h2>
      <p>
        Admin tools will live here (create entries, delete entries, select
        winners, and manage uploads).
      </p>
      <section className="admin-tools">
        <h3>Raffle Tools</h3>
        <div className="admin-actions">
          <Link to="/admin/raffle-winner" className="admin-action-link">
            Pick Random Winner
          </Link>
          <button type="button" onClick={seedEntries}>
            Seed Entries
          </button>
          <button type="button" onClick={() => setConfirmDeleteOpen(true)}>
            Delete All Entries
          </button>
        </div>
        {raffleStatus && (
          <div
            className={
              raffleStatusType === 'error'
                ? 'error-banner'
                : 'success-banner'
            }
          >
            {raffleStatus}
          </div>
        )}
        {confirmDeleteOpen && (
          <div className="confirm-panel">
            <h3>Delete All Entries?</h3>
            <p>This will permanently delete every raffle entry.</p>
            <div className="confirm-actions">
              <button type="button" onClick={() => setConfirmDeleteOpen(false)}>
                Cancel
              </button>
              <button type="button" onClick={deleteAllEntries}>
                Delete
              </button>
            </div>
          </div>
        )}
      </section>
      <PartnershipSettingsAdmin />
      <ServiceSpecsAdmin />
      <section className="admin-module-pricing">
        <h3>Raffle Entry Settings</h3>
        {settingsStatus && (
          <div
            className={
              settingsStatusType === 'error' ? 'error-banner' : 'success-banner'
            }
          >
            {settingsStatus}
          </div>
        )}
        {settingsLoading ? (
          <p>Loading raffle settings...</p>
        ) : (
          <div className="admin-settings-form">
            <label className="settings-full-width">
              Raffle Video URL
              <input
                type="url"
                value={raffleSettings.videoUrl}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    videoUrl: event.target.value,
                  }))
                }
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </label>
            <label className="settings-full-width">
              YouTube Card Description
              <textarea
                rows={2}
                value={raffleSettings.descriptions.youtube}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    descriptions: {
                      ...prev.descriptions,
                      youtube: event.target.value,
                    },
                  }))
                }
              />
            </label>
            <label className="settings-full-width">
              Instagram Card Description
              <textarea
                rows={2}
                value={raffleSettings.descriptions.instagram}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    descriptions: {
                      ...prev.descriptions,
                      instagram: event.target.value,
                    },
                  }))
                }
              />
            </label>
            <label className="settings-full-width">
              Website Signup Card Description
              <textarea
                rows={2}
                value={raffleSettings.descriptions.website}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    descriptions: {
                      ...prev.descriptions,
                      website: event.target.value,
                    },
                  }))
                }
              />
            </label>
            <label className="settings-full-width">
              Facebook Card Description
              <textarea
                rows={2}
                value={raffleSettings.descriptions.facebook}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    descriptions: {
                      ...prev.descriptions,
                      facebook: event.target.value,
                    },
                  }))
                }
              />
            </label>
            <label className="settings-full-width">
              Purchase Card Description
              <textarea
                rows={2}
                value={raffleSettings.descriptions.purchase}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    descriptions: {
                      ...prev.descriptions,
                      purchase: event.target.value,
                    },
                  }))
                }
              />
            </label>
            <label className="checkbox-field settings-full-width">
              <input
                type="checkbox"
                checked={Boolean(raffleSettings.sponsor.enabled)}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    sponsor: {
                      ...prev.sponsor,
                      enabled: event.target.checked,
                    },
                  }))
                }
              />
              Enable Sponsor Entry
            </label>
            <label>
              Sponsor Name
              <input
                type="text"
                value={raffleSettings.sponsor.name}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    sponsor: { ...prev.sponsor, name: event.target.value },
                  }))
                }
                placeholder="AW Italian"
              />
            </label>
            <label>
              Sponsor Logo URL
              <input
                type="url"
                value={raffleSettings.sponsor.logoUrl}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    sponsor: { ...prev.sponsor, logoUrl: event.target.value },
                  }))
                }
                placeholder="https://..."
              />
            </label>
            <label className="settings-full-width">
              Sponsor Entry Instructions
              <textarea
                rows={3}
                value={raffleSettings.sponsor.instructions}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    sponsor: {
                      ...prev.sponsor,
                      instructions: event.target.value,
                    },
                  }))
                }
                placeholder="Tell users what they need to do for this entry."
              />
            </label>
            <label>
              Purchase Entries
              <input
                type="number"
                min="1"
                step="1"
                value={raffleSettings.purchase.entries}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    purchase: {
                      ...prev.purchase,
                      entries: event.target.value,
                    },
                  }))
                }
              />
            </label>
            <label>
              Purchase Price (USD)
              <input
                type="number"
                min="0"
                step="0.01"
                value={raffleSettings.purchase.priceUsd}
                onChange={(event) =>
                  setRaffleSettings((prev) => ({
                    ...prev,
                    purchase: {
                      ...prev.purchase,
                      priceUsd: event.target.value,
                    },
                  }))
                }
              />
            </label>
            <button
              type="button"
              onClick={saveRaffleSettings}
              disabled={settingsSaving}
            >
              {settingsSaving ? 'Saving...' : 'Save Raffle Settings'}
            </button>
            {settingsSavedAt && (
              <div className="module-key">Last saved: {settingsSavedAt}</div>
            )}
          </div>
        )}
      </section>
      <section className="admin-requests">
        <h3>Entry Requests</h3>
        {requests.length === 0 && <p>No pending requests.</p>}
        {requests.map((request) => (
          <div key={request._id} className="request-row">
            <div>
              <strong>{request.email}</strong>
              <div className="module-key">{request.method}</div>
            </div>
            <div className="request-actions">
              <span className="request-status">{request.status}</span>
              <button
                type="button"
                onClick={() => approveRequest(request._id)}
                disabled={request.status === 'approved'}
              >
                Approve
              </button>
              <button type="button" onClick={() => deleteRequest(request._id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </section>
      <section className="admin-module-pricing">
        <h3>EEPROM Module Pricing</h3>
        {error && <div className="error-banner">{error}</div>}
        {modules.length === 0 && <p>No modules loaded yet.</p>}
        {modules.map((module) => (
          <div key={module.moduleKey} className="module-row">
            <div>
              <strong>{module.name}</strong>
              <div className="module-key">{module.moduleKey}</div>
            </div>
            <div className="module-price">
              <label>Price (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                defaultValue={(module.priceCents / 100).toFixed(2)}
                onBlur={(event) => {
                  const value = Number(event.target.value);
                  const cents = Math.round(value * 100);
                  if (!Number.isFinite(cents)) return;
                  updatePrice(module.moduleKey, cents);
                }}
              />
              <button
                type="button"
                disabled={saving}
                onClick={(event) => {
                  const input =
                    event.currentTarget.parentElement.querySelector('input');
                  if (!input) return;
                  const value = Number(input.value);
                  const cents = Math.round(value * 100);
                  if (!Number.isFinite(cents)) return;
                  updatePrice(module.moduleKey, cents);
                }}
              >
                Save
              </button>
            </div>
          </div>
        ))}
      </section>
      <Home
        showStats={false}
        entryAction={<Link to="/raffle/enter">Create Entry</Link>}
        refreshKey={entriesRefresh}
      />
    </div>
  );
};

export default Admin;
